import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import { amplifyInitializer } from 'src/app/@configs/amplify-init';
import { appIconsInitializer } from 'src/app/@configs/app-icons.init';
import { appMixpanelInitializer } from 'src/app/@configs/app-mixpanel.init';
import { appRouteTrackingInitializer } from 'src/app/@configs/app-route-tracking.init';
import { appScrollTrackingInitializer } from 'src/app/@configs/app-scroll-tracking.init';
import { authInterceptor } from 'src/app/@interceptors/auth.interceptor';
import { withAppBaseHref } from 'src/app/@utils/app-base-href';
import { routes } from './app.routes';

/**
 * NgOptimizedImage loader: normalizes relative `./../../../assets/...` paths to
 * root-absolute `/assets/...` and prefixes the app's &lt;base href&gt; (e.g.
 * `/reports`) so requests resolve under the parent CloudFront aggregator.
 */
function synopsisReportsImageLoader(config: ImageLoaderConfig): string {
  const src = config.src ?? '';
  if (!src) {
    return src;
  }
  if (
    /^https?:\/\//i.test(src) ||
    src.startsWith('//') ||
    src.startsWith('data:') ||
    src.startsWith('blob:')
  ) {
    return src;
  }
  const assetsIdx = src.indexOf('assets/');
  const normalized = assetsIdx >= 0 ? `/${src.slice(assetsIdx)}` : src;
  return withAppBaseHref(normalized);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: IMAGE_LOADER, useValue: synopsisReportsImageLoader },
    provideRouter(
      routes,
      withViewTransitions(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      })
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAppInitializer(() => {
      const initializerFn = amplifyInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appIconsInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appMixpanelInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appRouteTrackingInitializer();
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = appScrollTrackingInitializer();
      return initializerFn();
    }),
    provideAnimationsAsync(),
  ],
};
